import { useState, useEffect } from 'react';
import { buildApiUrl } from '../config/api';

interface ErrorNotification {
  id: string;
  error_id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  title: string;
  message: string;
  status: 'open' | 'investigating' | 'resolved' | 'ignored';
  created_at: string;
  isRead?: boolean;
}

export const useErrorNotifications = () => {
  const [notifications, setNotifications] = useState<ErrorNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Check if user has developer/admin access
  const hasErrorAccess = () => {
    try {
      const userRole = localStorage.getItem('user_role');
      return ['admin', 'developer'].includes(userRole || '');
    } catch {
      return false;
    }
  };

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetch(buildApiUrl('/api/errors?limit=20'), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const errorNotifications = (data.errors || []).map((error: any) => ({
          ...error,
          isRead: error.status !== 'open'
        }));
        setNotifications(errorNotifications);
        setUnreadCount(errorNotifications.filter((n: ErrorNotification) => !n.isRead).length);
      }
    } catch (error) {
      console.error('Failed to load error notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (errorId: string) => {
    try {
      const response = await fetch(buildApiUrl(`/api/errors/${errorId}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify({ status: 'investigating' })
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.error_id === errorId ? { ...n, isRead: true, status: 'investigating' } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark error as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);
      
      for (const notification of unreadNotifications) {
        await fetch(buildApiUrl(`/api/errors/${notification.error_id}`), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
          },
          body: JSON.stringify({ status: 'investigating' })
        });
      }

      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, status: 'investigating' })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all errors as read:', error);
    }
  };

  useEffect(() => {
    // Only load and poll for admin/developer users
    if (!hasErrorAccess()) {
      return;
    }
    
    loadNotifications();
    
    // Auto-refresh every 5 minutes, only in development for admin/developer
    const interval = import.meta.env.DEV
      ? setInterval(() => {
          if (hasErrorAccess()) {
            loadNotifications();
          }
        }, 300000) // 5 minutes
      : null;
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refresh: loadNotifications
  };
};